import React from "react";
import { useSearchParams } from "react-router-dom";

/**
 * A page that hosts multiple sub-views behind a segmented tab control.
 * Tab state is synced to the `?tab=` query param so views are deep-linkable
 * (e.g. /app/resume?tab=build) and the browser back button works.
 *
 * props.tabs: [{ key, label, icon?, render: () => ReactNode }]
 * props.defaultKey: optional — falls back to tabs[0].key
 */
export default function TabbedPage({ tabs, defaultKey }) {
  const [params, setParams] = useSearchParams();
  const active =
    tabs.find((t) => t.key === params.get("tab"))?.key ??
    defaultKey ??
    tabs[0]?.key;

  const setActive = (key) => {
    const next = new URLSearchParams(params);
    next.set("tab", key);
    setParams(next, { replace: false });
  };

  const activeTab = tabs.find((t) => t.key === active) ?? tabs[0];

  return (
    <div>
      {/* Tab bar */}
      <div className="sticky top-0 z-20 bg-[#FDFBF7]/90 backdrop-blur border-b border-stone-200">
        <div className="container-x">
          <div className="flex gap-1 py-3 overflow-x-auto" style={{ scrollbarWidth: "none" }}>
            {tabs.filter((t) => !t.hidden).map((t) => {
              const isActive = t.key === active;
              return (
                <button
                  key={t.key}
                  onClick={() => setActive(t.key)}
                  data-testid={`tab-${t.key}`}
                  className={`relative inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                    isActive
                      ? "bg-stone-900 text-stone-50"
                      : "text-stone-500 hover:text-stone-900 hover:bg-stone-100"
                  }`}
                >
                  {t.icon && <t.icon size={15} />}
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Active view */}
      <div key={active}>{activeTab?.render()}</div>
    </div>
  );
}
